import { uiFormat, uiText } from './text.js';
import './kit.css';

/**
 * The small pieces the character windows share (profile, inventory): an
 * icon of `assets/ui/icon-<name>.png`, a heading with its rule, lines of
 * « icon · label · value », counters in chips and the level seal. One look
 * for every window that shows the character; the numbers arrive computed.
 */

/** One line of a window: an icon, a label, a value, and an optional note after it. */
export interface KitLine {
  icon: string;
  label: string;
  value: string;
  /** Beside the value, in green (a bonus, a share), or grey with `quietNote`. */
  note?: string;
  quietNote?: boolean;
  /** A tooltip for the whole line. */
  title?: string;
  /** The value's colour, when it says something (an upgrade level). */
  tone?: string;
}

export function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = uiText(text);
  return node;
}

export function kitIcon(name: string): HTMLElement {
  const node = element('span', 'kit-icon');
  node.style.backgroundImage = `url('/assets/ui/icon-${name}.png')`;
  node.setAttribute('aria-hidden', 'true');
  return node;
}

/** A heading with a rule running out to the right; `aside` sits at its end. */
export function kitHeading(title: string, aside?: string): HTMLElement {
  const heading = element('h3', 'kit-heading', title);
  if (aside !== undefined) heading.append(element('span', 'kit-heading__aside', aside));
  return heading;
}

export function kitLines(entries: readonly KitLine[]): HTMLElement {
  const list = element('dl', 'kit-lines');
  for (const entry of entries) {
    const row = element('div', 'kit-line');
    if (entry.title !== undefined) row.title = uiText(entry.title);
    const value = element('dd', 'kit-line__value', entry.value);
    if (entry.tone !== undefined) value.style.color = entry.tone;
    if (entry.note !== undefined) {
      const quiet = entry.quietNote === true ? ' kit-line__note--quiet' : '';
      value.append(element('span', `kit-line__note${quiet}`, entry.note));
    }
    row.append(kitIcon(entry.icon), element('dt', 'kit-line__label', entry.label), value);
    list.append(row);
  }
  return list;
}

/** A counter in a chip (gold, portals): the label is its tooltip and screen-reader text. */
export function kitChip(line: KitLine): HTMLElement {
  const chip = element('div', 'kit-chip');
  chip.title = uiText(line.label);
  chip.append(kitIcon(line.icon), element('span', 'kit-sr', line.label));
  chip.append(element('span', 'kit-chip__value', line.value));
  return chip;
}

/** The level as a gold seal. */
export function kitSeal(level: number): HTMLElement {
  const seal = element('span', 'kit-seal');
  seal.textContent = String(level);
  seal.title = uiFormat('Niveau {level}', { level });
  return seal;
}

/** The character standing on a patch of the world: put the doll's canvas in it. */
export function kitStage(extraClass = ''): HTMLElement {
  return element('div', `kit-stage ${extraClass}`.trim());
}

/**
 * A window's title, as every window wears it: the icon of the window (the
 * one of its HUD button when it has one) before a large green title. The
 * icon is a `::before` fed by a CSS variable, so a panel that rewrites its
 * title's text (a trade naming the other player) keeps it.
 */
export function kitTitle<T extends HTMLElement>(title: T, icon: string): T {
  title.classList.add('kit-title');
  title.style.setProperty('--kit-title-icon', `url('/assets/ui/icon-${icon}.png')`);
  return title;
}
